// NEW_FILE_JUSTIFIED: Email list component with tabs, search, and pagination — no existing component handles Gmail-style table email display
import { useCallback, useMemo } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Send,
  Pencil,
  Loader2,
  Mail,
  MailOpen,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmailRow, DraftRow } from './EmailRow';
import type { IEmailListProps, TGmailTab } from '@/@types/gmail';

export function EmailList({
  emails,
  drafts,
  activeTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  pagination,
  onPageChange,
  selectedIds,
  onSelectionChange,
  onEmailClick,
  onDelete,
  isDeleting,
  onMarkReadStatus,
  isMarkingReadStatus,
  isLoading,
  onCreateLead,
  canWrite = true,
}: IEmailListProps) {
  // Determine items for current tab
  const currentItems = activeTab === 'drafts' ? drafts : emails;
  const totalItems = activeTab === 'drafts' ? drafts.length : pagination.total;

  // Select all logic
  const allSelected = useMemo(() => {
    if (currentItems.length === 0) return false;
    return currentItems.every(item => selectedIds.has(item.id));
  }, [currentItems, selectedIds]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const allIds = new Set(currentItems.map(item => item.id));
        onSelectionChange(allIds);
      } else {
        onSelectionChange(new Set());
      }
    },
    [currentItems, onSelectionChange]
  );

  const handleSelectItem = useCallback(
    (id: string, selected: boolean) => {
      const newIds = new Set(selectedIds);
      if (selected) {
        newIds.add(id);
      } else {
        newIds.delete(id);
      }
      onSelectionChange(newIds);
    },
    [selectedIds, onSelectionChange]
  );

  // Determine if selected emails should be marked as read or unread.
  // If any selected email is unread → show "Mark read"; if all read → show "Mark unread".
  const shouldMarkAsRead = useMemo(() => {
    if (activeTab === 'drafts' || selectedIds.size === 0) return true;
    return emails.filter(e => selectedIds.has(e.id)).some(e => !e.isRead);
  }, [emails, selectedIds, activeTab]);

  const handleDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    onDelete(Array.from(selectedIds));
  }, [selectedIds, onDelete]);

  const handleMarkReadStatus = useCallback(() => {
    if (selectedIds.size === 0) return;
    onMarkReadStatus(Array.from(selectedIds), shouldMarkAsRead);
  }, [selectedIds, onMarkReadStatus, shouldMarkAsRead]);

  const handleTabChange = useCallback(
    (value: string) => {
      onTabChange(value as TGmailTab); // AS_JUSTIFIED: TabsTrigger values are constrained to TGmailTab literals defined in TabsTrigger value props
    },
    [onTabChange]
  );

  // Pagination
  const pageStart = pagination.page * pagination.pageSize + 1;
  const pageEnd = Math.min((pagination.page + 1) * pagination.pageSize, totalItems);

  const tableAriaLabel =
    activeTab === 'drafts' ? 'Drafts' : activeTab === 'sent' ? 'Sent emails' : 'Inbox emails';

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      className="flex flex-1 flex-col overflow-hidden"
    >
      {/* Header: Tabs + Search + Pagination */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2 shrink-0">
        <div className="flex items-center gap-4">
          <TabsList>
            <TabsTrigger value="inbox">Inbox</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            {canWrite && <TabsTrigger value="drafts">Drafts</TabsTrigger>}
          </TabsList>

          {/* Bulk actions */}
          {canWrite && selectedIds.size > 0 && activeTab !== 'drafts' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkReadStatus}
              disabled={isMarkingReadStatus}
              aria-label={
                isMarkingReadStatus
                  ? 'Updating read status…'
                  : shouldMarkAsRead
                    ? `Mark ${selectedIds.size} email${selectedIds.size === 1 ? '' : 's'} as read`
                    : `Mark ${selectedIds.size} email${selectedIds.size === 1 ? '' : 's'} as unread`
              }
              aria-busy={isMarkingReadStatus}
            >
              {isMarkingReadStatus ? (
                <Loader2 size={14} className="mr-1 animate-spin" aria-hidden="true" />
              ) : shouldMarkAsRead ? (
                <MailOpen size={14} className="mr-1" aria-hidden="true" />
              ) : (
                <Mail size={14} className="mr-1" aria-hidden="true" />
              )}
              {isMarkingReadStatus ? 'Updating...' : shouldMarkAsRead ? 'Mark read' : 'Mark unread'}
            </Button>
          )}
          {canWrite && selectedIds.size > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-destructive"
            >
              {isDeleting ? (
                <Loader2 size={14} className="mr-1 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 size={14} className="mr-1" aria-hidden="true" />
              )}
              {isDeleting ? 'Deleting...' : `Delete (${selectedIds.size})`}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              placeholder="Search emails..."
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              className="h-8 w-48 pl-8 text-sm"
              aria-label="Search emails"
            />
          </div>

          {/* Pagination */}
          {totalItems > 0 && activeTab !== 'drafts' && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>
                {pageStart}-{pageEnd} of {totalItems}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={pagination.page === 0}
                onClick={() => onPageChange(pagination.page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={pageEnd >= totalItems}
                onClick={() => onPageChange(pagination.page + 1)}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tab panel — value matches activeTab so it is always visible */}
      <TabsContent value={activeTab} forceMount className="flex-1 overflow-auto mt-0">
        {isLoading ? (
          <div role="status" aria-label="Loading emails" className="flex flex-col">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-4 w-32 shrink-0" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-3 w-12 shrink-0" />
              </div>
            ))}
          </div>
        ) : currentItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            {activeTab === 'inbox' && (
              <Inbox size={32} className="text-muted-foreground/40" aria-hidden="true" />
            )}
            {activeTab === 'sent' && (
              <Send size={32} className="text-muted-foreground/40" aria-hidden="true" />
            )}
            {activeTab === 'drafts' && (
              <Pencil size={32} className="text-muted-foreground/40" aria-hidden="true" />
            )}
            <span className="text-sm text-muted-foreground">
              {activeTab === 'inbox' && 'No emails in inbox'}
              {activeTab === 'sent' && 'No sent emails'}
              {activeTab === 'drafts' && 'No drafts'}
            </span>
          </div>
        ) : (
          <Table aria-label={tableAriaLabel} className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 px-3">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all emails"
                  />
                </TableHead>
                <TableHead className="w-40 px-3">
                  {activeTab === 'drafts' ? 'To' : 'From'}
                </TableHead>
                <TableHead className="px-3">Subject</TableHead>
                <TableHead className="w-24 px-3 text-right">Date</TableHead>
                <TableHead className="w-10 px-1" aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeTab === 'drafts'
                ? drafts.map(draft => (
                    <DraftRow
                      key={draft.id}
                      draft={draft}
                      isSelected={selectedIds.has(draft.id)}
                      onSelect={handleSelectItem}
                      onClick={onEmailClick}
                    />
                  ))
                : emails.map(email => (
                    <EmailRow
                      key={email.id}
                      email={email}
                      isSelected={selectedIds.has(email.id)}
                      onSelect={handleSelectItem}
                      onClick={onEmailClick}
                      onCreateLead={onCreateLead}
                    />
                  ))}
            </TableBody>
          </Table>
        )}
      </TabsContent>
    </Tabs>
  );
}
